/**
 * Temporary image file housekeeping (native only).
 *
 * expo-image-picker copies every selected photo into `<cache>/ImagePicker`, and
 * expo-image-manipulator leaves its outputs in `<cache>/ImageManipulator`.
 * Nothing else cleans these up, so on iOS (no OS-level "clear cache") they
 * accumulate, one full-resolution copy per picked photo. Pattern borrowed from
 * social-app `src/state/gallery.ts` (purgeTemporaryImageFiles).
 *
 * Check-in draft photos would be lost by that purge, so drafts keep their own
 * copies under `<cache>/flavrly-drafts/<draftId>/`, which the purge never touches.
 *
 * Every export is a no-op on web (blob: URIs and the browser cache are not ours
 * to manage) and never throws: housekeeping must not break a submit or a save.
 */

import { Directory, File, Paths } from 'expo-file-system';
import { Platform } from 'react-native';

/** Cache sub-directories owned by the expo picker and manipulator modules. */
export const SYSTEM_MEDIA_CACHE_DIRS = ['ImagePicker', 'ImageManipulator'] as const;

/** Cache sub-directory holding stable copies of check-in draft photos. */
export const DRAFT_PHOTOS_DIR_NAME = 'flavrly-drafts';

function isNative(): boolean {
  return Platform.OS !== 'web';
}

function deleteDirectoryIfExists(directory: Directory): void {
  if (directory.exists) {
    directory.delete();
  }
}

/**
 * Delete the picker and manipulator cache directories. The expo modules
 * recreate them on demand. Call after a check-in submit or discard.
 */
export async function purgeTemporaryImageFiles(): Promise<void> {
  if (!isNative()) return;

  for (const name of SYSTEM_MEDIA_CACHE_DIRS) {
    try {
      deleteDirectoryIfExists(new Directory(Paths.cache, name));
    } catch (error) {
      console.warn(`Failed to purge temporary image directory ${name}:`, error);
    }
  }
}

function draftPhotosDirectory(draftId: string): Directory {
  return new Directory(Paths.cache, DRAFT_PHOTOS_DIR_NAME, draftId);
}

/** Only local files can (and need to) be copied; remote or content URIs stay as-is. */
function isLocalFileUri(uri: string): boolean {
  return uri.startsWith('file://');
}

function uniqueFileName(sourceUri: string, index: number): string {
  const baseName = sourceUri.split('/').pop() || 'photo.jpg';
  const suffix = Math.random().toString(36).substring(2, 8);
  return `${Date.now()}-${index}-${suffix}-${baseName}`;
}

/** Result of copying one photo: the URI to persist and whether the copy failed. */
interface CopyOutcome {
  uri: string;
  failed: boolean;
}

async function copyIntoDirectory(
  directory: Directory,
  uri: string,
  index: number
): Promise<CopyOutcome> {
  if (!isLocalFileUri(uri) || uri.startsWith(directory.uri)) return { uri, failed: false };

  try {
    const destination = new File(directory, uniqueFileName(uri, index));
    await new File(uri).copy(destination);
    return { uri: destination.uri, failed: false };
  } catch (error) {
    // Keep the original URI: the draft still works until the cache is purged,
    // but the caller must tell the user the photo may not survive.
    console.warn('Failed to copy draft photo into stable storage:', error);
    return { uri, failed: true };
  }
}

function pruneUnreferencedFiles(directory: Directory, keep: ReadonlySet<string>): void {
  for (const item of directory.list()) {
    if (item instanceof File && !keep.has(item.uri)) {
      item.delete();
    }
  }
}

export interface PersistDraftPhotosResult {
  /** URIs to store, in input order and of the same length. */
  uris: string[];
  /** Local photos that could not be copied (their original URI is kept). */
  failedCount: number;
}

/**
 * Copy a draft's photos into `<cache>/flavrly-drafts/<draftId>/` and return
 * the URIs to persist (same order, same length) plus how many local photos
 * could not be copied. Photos already in the draft directory are kept; files
 * no longer referenced by the draft are removed. On web, returns the input
 * unchanged. If the directory cannot be prepared, every local file counts as
 * failed.
 */
export async function persistDraftPhotos(
  draftId: string,
  uris: readonly string[]
): Promise<PersistDraftPhotosResult> {
  if (!isNative() || uris.length === 0) return { uris: [...uris], failedCount: 0 };

  try {
    const directory = draftPhotosDirectory(draftId);
    directory.create({ intermediates: true, idempotent: true });

    const outcomes = await Promise.all(
      uris.map((uri, index) => copyIntoDirectory(directory, uri, index))
    );
    const persisted = outcomes.map((outcome) => outcome.uri);

    try {
      pruneUnreferencedFiles(directory, new Set(persisted));
    } catch (error) {
      console.warn('Failed to prune stale draft photos:', error);
    }

    return { uris: persisted, failedCount: outcomes.filter((o) => o.failed).length };
  } catch (error) {
    console.warn('Failed to prepare draft photo directory:', error);
    return { uris: [...uris], failedCount: uris.filter(isLocalFileUri).length };
  }
}

/** Delete every stored photo of a draft (after submit or discard). */
export async function deleteDraftPhotos(draftId: string): Promise<void> {
  if (!isNative()) return;

  try {
    deleteDirectoryIfExists(draftPhotosDirectory(draftId));
  } catch (error) {
    console.warn('Failed to delete draft photos:', error);
  }
}

/**
 * Delete the photo directories of every draft (`<cache>/flavrly-drafts/`), for
 * when all drafts are cleared at once. Never throws; no-op on web.
 */
export async function deleteAllDraftPhotos(): Promise<void> {
  if (!isNative()) return;

  try {
    deleteDirectoryIfExists(new Directory(Paths.cache, DRAFT_PHOTOS_DIR_NAME));
  } catch (error) {
    console.warn('Failed to delete draft photo directories:', error);
  }
}

/**
 * Whether a persisted local photo still exists. The OS may evict cache files
 * under storage pressure. Non-file URIs are assumed to exist. Always true on web.
 */
export function draftPhotoExists(uri: string): boolean {
  if (!isNative() || !isLocalFileUri(uri)) return true;

  try {
    return new File(uri).exists;
  } catch {
    return false;
  }
}
